// "Which store am I in?" from whatever the device can tell: a Bluetooth beacon or Wi-Fi network
// that belongs to a store is a sure sign; otherwise the nearest store whose area the GPS fix falls in.
// Browsers can only offer GPS; Wi-Fi names and beacons come from native apps.

import type { Item, Store } from './types';

export interface PresenceCues {
  position?: { lat: number; lng: number; accuracyM?: number } | null;
  wifiNames?: string[];
  beacons?: string[];
}

export interface Presence {
  store: Store;
  via: 'beacon' | 'wifi' | 'gps';
  /** Metres from the store (GPS only). */
  distanceM?: number;
}

/** Great-circle distance in metres. */
export function distanceM(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const R = 6_371_000;
  const rad = (d: number) => (d * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

const norm = (s: string) => s.trim().toLowerCase();

export function detectStore(stores: Store[], cues: PresenceCues): Presence | null {
  const beacons = new Set((cues.beacons ?? []).map(norm));
  if (beacons.size) {
    const s = stores.find((x) => x.beacons.some((b) => beacons.has(norm(b))));
    if (s) return { store: s, via: 'beacon' };
  }
  const wifi = new Set((cues.wifiNames ?? []).map(norm));
  if (wifi.size) {
    const s = stores.find((x) => x.wifiNames.some((w) => wifi.has(norm(w))));
    if (s) return { store: s, via: 'wifi' };
  }
  const p = cues.position;
  if (p) {
    // a fuzzy fix gets a little slack, but a fix a kilometre wide says nothing
    if ((p.accuracyM ?? 0) > 500) return null;
    let best: Presence | null = null;
    for (const s of stores) {
      if (!s.location) continue;
      const d = distanceM(p, s.location);
      const reach = s.location.radiusM + Math.min(p.accuracyM ?? 0, 100);
      if (d <= reach && (!best || d < best.distanceM!)) best = { store: s, via: 'gps', distanceM: Math.round(d) };
    }
    return best;
  }
  return null;
}

/** Shown when shopping at this store: items for any store, or marked for this one. Null = every item. */
export function itemAtStore(item: Pick<Item, 'storeIds'>, storeId: string | null): boolean {
  return !storeId || item.storeIds.length === 0 || item.storeIds.includes(storeId);
}
